import { Box, Modal, Slide } from '@material-ui/core'
import {
  DocumentShare,
  OrganizationPolicySetting,
  RequestContactSettingFieldValue,
  SiteShare
} from '@noco/http-client/lib/noco'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import { Button, Colors, Typography } from 'src/components/atoms'
import { SettingInputRow } from 'src/components/molecules'
import { PolicySentence } from 'src/components/molecules/PolicySentence'
import {
  ContactSettingValueMap,
  convertFromContactSettingValueMap,
  hasEmailSettingInShareForms,
  initializeContactSettingValueMap
} from 'src/fixtures/modules/contact/utils'
import { VisitorAuthModalBase } from '../VisitorAuthModalBase'

interface ModalVisitorConnectToContactProps {
  policySettings?: OrganizationPolicySetting[]
  documentId?: string
  siteId?: string
  open: boolean
  onClose: () => void
  onSubmit: (param: RequestContactSettingFieldValue[] | undefined) => void | Promise<void>
  documentShareForms?: DocumentShare['documentShareForms']
  siteShareForms?: SiteShare['siteShareForms']
  organizationName?: string
  materialTitle?: string
}

type FieldErrorMap = { [settingId: string]: { [fieldId: string]: string } }

const emailValidationSchema = yup
  .string()
  .email('メールアドレスの形式で入力してください')
  .required('メールアドレスは必須です')

type ShareFormItem =
  | NonNullable<DocumentShare['documentShareForms']>[number]
  | NonNullable<SiteShare['siteShareForms']>[number]

const validateForms = (forms: ShareFormItem[] | undefined, values: ContactSettingValueMap): FieldErrorMap => {
  const errors: FieldErrorMap = {}
  forms?.forEach(form => {
    const setting = form.contactSetting
    const settingId = setting?.id
    if (!setting || !settingId) return
    setting.contactSettingFields?.forEach(field => {
      const fieldId = field.id
      if (!fieldId) return
      if (field.isHideOnEu) return
      const value = values[settingId]?.[fieldId]

      if (setting.settingType === 'email') {
        try {
          emailValidationSchema.validateSync(typeof value === 'string' ? value.trim() : value)
        } catch (err) {
          const message = (err as yup.ValidationError).errors[0]
          if (!errors[settingId]) errors[settingId] = {}
          errors[settingId][fieldId] = message
        }
        return
      }

      if (form.isRequired) {
        const isEmpty = Array.isArray(value) ? value.length === 0 : !value
        if (isEmpty) {
          if (!errors[settingId]) errors[settingId] = {}
          errors[settingId][fieldId] = '必須項目です'
        }
      }
    })
  })
  return errors
}

const hasErrors = (errors: FieldErrorMap): boolean =>
  Object.values(errors).some(fieldMap => Object.keys(fieldMap).length > 0)

export const ModalVisitorConnectToContact = ({
  documentId,
  siteId,
  open,
  onClose,
  onSubmit,
  documentShareForms,
  siteShareForms,
  policySettings,
  organizationName,
  materialTitle
}: ModalVisitorConnectToContactProps) => {
  const [settingValues, setSettingValues] = useState<ContactSettingValueMap>({})
  const settingValueKeys = useMemo(() => Object.keys(settingValues), [settingValues])
  const [isInit, setIsInit] = useState(false)
  const [errors, setErrors] = useState<FieldErrorMap>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()
  const targetForms = (siteShareForms ?? documentShareForms) as ShareFormItem[] | undefined
  const hasEmailField = useMemo(() => hasEmailSettingInShareForms(targetForms), [targetForms])

  // 初期化
  useEffect(() => {
    const settings = siteShareForms
      ? siteShareForms.map(item => item.contactSetting!)
      : documentShareForms?.map(item => item.contactSetting!)
    if (open && settings && settingValueKeys.length === 0 && !isInit) {
      const initalSettingValueMap = initializeContactSettingValueMap(settings, true)
      setSettingValues(initalSettingValueMap)
      setIsInit(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentShareForms, settingValues, siteId, siteShareForms, settingValueKeys.length])

  // フォームの差分追従
  useEffect(() => {
    const settings = siteShareForms
      ? siteShareForms.map(item => item.contactSetting!)
      : documentShareForms?.map(item => item.contactSetting!)

    if (settingValueKeys.length > 0 && settings && settingValueKeys.length !== settings.length) {
      const keys = settingValueKeys.filter(valueId => settings.some(item => item.id === valueId))
      const newSettingsValues = initializeContactSettingValueMap(settings, true)
      keys.forEach(key => {
        newSettingsValues[key] = settingValues[key]
      })
      setSettingValues(newSettingsValues)
    }
  }, [documentShareForms, settingValues, siteId, siteShareForms, settingValueKeys.length, settingValueKeys])

  const handleUpdateValue = useCallback(
    (settingId: string, fieldId: string, value: any) => {
      const newValueMap = { ...settingValues }
      if (!newValueMap[settingId]) newValueMap[settingId] = {}
      newValueMap[settingId][fieldId] = value
      setSettingValues(newValueMap)
    },
    [settingValues]
  )

  const handleSubmit = useCallback(async () => {
    if (submitting) return
    setSubmitError(undefined)

    const validationErrors = validateForms(targetForms, settingValues)
    setErrors(validationErrors)
    if (hasErrors(validationErrors)) return

    try {
      setSubmitting(true)
      const contactSettingFieldValues = convertFromContactSettingValueMap(settingValues)
      await onSubmit?.(contactSettingFieldValues)
      setIsInit(false)
    } catch (err) {
      console.error(err)
      setSubmitError('送信に失敗しました。通信状態をご確認のうえ、もう一度お試しください')
    } finally {
      setSubmitting(false)
    }
  }, [onSubmit, settingValues, submitting, targetForms])

  // モーダルを閉じた時に状態リセット
  useEffect(() => {
    if (!open) {
      setIsInit(false)
      setSettingValues({})
      setErrors({})
      setSubmitError(undefined)
      setSubmitting(false)
    }
  }, [open])

  const renderFieldErrors = (settingId: string | undefined) => {
    if (!settingId) return null
    const fieldErrors = errors[settingId]
    if (!fieldErrors) return null
    return (
      <Box mt="-18px" mb="18px">
        {Object.entries(fieldErrors).map(([fieldId, message]) => (
          <Box key={fieldId} color={Colors.error.default}>
            <Typography fontSize="s">{message}</Typography>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableBackdropClick
      disableEscapeKeyDown
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}
    >
      <Slide in={open} direction="up">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <VisitorAuthModalBase title={materialTitle || '資料の閲覧'}>
            <Box mb="20px" sx={{ textAlign: 'center' }}>
              <Typography fontSize="s" lineheight="18.48px" fontWeight={300}>
                {hasEmailField ? '資料の閲覧には、下記ご入力ください' : '続けてご入力をお願いいたします'}
              </Typography>
            </Box>

            {submitError && (
              <Box
                mb="16px"
                px="12px"
                py="8px"
                borderRadius="4px"
                border={`1px solid ${Colors.error.default}`}
                color={Colors.error.default}
              >
                <Typography fontSize="s">{submitError}</Typography>
              </Box>
            )}

            {documentId &&
              documentShareForms?.map(item => {
                const settingId = item.contactSetting?.id
                const values = settingId ? settingValues[settingId] : undefined
                return (
                  <Box key={item.id}>
                    <SettingInputRow
                      name={item.contactSetting?.name || 'name'}
                      kind="contact"
                      required={item.isRequired}
                      setting={item.contactSetting!}
                      values={values || {}}
                      onChange={data => handleUpdateValue(settingId!, data.fieldId, data.value)}
                      isEuView
                    />
                    {renderFieldErrors(settingId)}
                  </Box>
                )
              })}

            {siteId &&
              siteShareForms?.map(item => {
                const settingId = item.contactSetting?.id
                const values = settingId ? settingValues[settingId] : undefined
                return (
                  <Box key={item.id}>
                    <SettingInputRow
                      isEuView
                      name={item.contactSetting?.name || 'name'}
                      kind="contact"
                      required={item.isRequired}
                      setting={item.contactSetting!}
                      values={values || {}}
                      onChange={data => handleUpdateValue(settingId!, data.fieldId, data.value)}
                    />
                    {renderFieldErrors(settingId)}
                  </Box>
                )
              })}
            <PolicySentence policySettings={policySettings} organizationName={organizationName} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '18px' }}>
              <Button width="116px" title="閲覧する" onClick={handleSubmit} loading={submitting} />
            </Box>
          </VisitorAuthModalBase>
        </Box>
      </Slide>
    </Modal>
  )
}
